<template>
  <div class="app-shell">
    <header v-if="showAppChrome" class="app-header">
      <div class="container">
        <h1 class="logo">{{ $t('app.title') }}</h1>
        <div class="header-controls">
          <HeaderControls compact />
        </div>
      </div>
    </header>

    <main class="app-main" :class="{ 'fullscreen': isFullscreenPage || isDemoPage || (isLandingPage && themeStore.isDark) }">
      <router-view />
    </main>

    <footer v-if="showAppChrome" class="app-footer">
      <div class="container footer-inner">
        <p>&copy; 2024 {{ $t('app.title') }} &nbsp;·&nbsp; <router-link to="/about" class="footer-about-link">{{ $t('app.aboutLink') }}</router-link></p>
        <HeaderControls compact />
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import HeaderControls from './components/HeaderControls.vue'
import { useThemeStore } from './stores/theme'

const route = useRoute()
const themeStore = useThemeStore()

// Hide header/footer on fullscreen pages (Stream, Host, Play, Game)
const isFullscreenPage = computed(() => {
  const fullscreenPages = ['Stream', 'Host', 'Play', 'Game']
  return fullscreenPages.includes(route.name)
})

const isDemoPage = computed(() => route.name === 'DemoPlay')
const isLandingPage = computed(() => route.name === 'Home')

// Home renders its own header/nav in both themes — hide app chrome there.
const showAppChrome = computed(() => !isFullscreenPage.value && !isDemoPage.value && !isLandingPage.value)
</script>

<style scoped>
.app-shell {
  width: 100%;
  min-height: 100vh;
}

.app-header {
  background: var(--koh-bg-app-header);
  color: var(--koh-text-on-hero);
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-family: var(--koh-font-display);
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

html[data-theme="dark"] .logo {
  text-shadow: var(--koh-hero-text-shadow);
}

.header-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.app-main {
  min-height: calc(100vh - 140px);
  background: var(--koh-bg-main);
}

.app-main.fullscreen {
  min-height: 100vh;
  width: 100%;
  padding: 0;
  margin: 0;
  background: var(--koh-body-bg);
}

.app-footer {
  background: var(--koh-bg-footer);
  color: var(--koh-text-on-hero);
  text-align: center;
  padding: 1rem 0;
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.footer-inner p {
  margin: 0;
}

.app-footer p {
  margin: 0;
  opacity: 0.8;
}

.footer-about-link {
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.footer-about-link:hover {
  color: white;
}

@media (max-width: 768px) {
  .app-header {
    display: none;
  }
}
</style>



